import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, Loader2, RefreshCw } from "lucide-react";

const PHRASE = "DELETE ALL USER ACCOUNTS";

// Beta-phase tool (super admin only): returns the USER database to a clean state for onboarding
// tests. Manual and typed-confirmation only — nothing destructive ever runs automatically.
export const AdminUserDataReset = () => {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState("");

  const load = () => api.get("/admin/user-data-status").then((r) => setS(r.data)).catch(() => setS(null));
  useEffect(() => { load(); }, []);

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/reset-user-data", { confirm: PHRASE });
      toast.success(`Done — ${data.customers_left} customer and ${data.cleaners_left} cleaner accounts remain.`);
      setConfirm("");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!s) return null;

  return (
    <div className="bg-card rounded-2xl border border-destructive/30 p-6" data-testid="admin-user-data-reset">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-5 w-5 text-destructive" />
        <h3 className="font-display font-semibold">Pre-launch account cleanup</h3>
        <span className="text-xs px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">Super admin · beta testing</span>
        <button onClick={load} className="ml-auto text-muted-foreground hover:text-accent" title="Refresh" data-testid="reset-refresh">
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>
      <p className="text-sm text-muted-foreground mt-2">
        This database holds <strong data-testid="reset-counts">{s.customers} customer</strong> and{" "}
        <strong>{s.cleaners} cleaner</strong> accounts ({s.cleaner_profiles} profiles, {s.bookings} bookings,
        {" "}{s.admins} admin). Last cleanup: <strong>{s.last_cleanup_at || "never"}</strong> · db: {s.db_name}
      </p>
      <p className="text-sm text-muted-foreground mt-3">
        Deletes every customer and cleaner account plus their login records, profiles, availability,
        bookings, messages and documents. Admin accounts, services, pricing, Stripe, Premium, email
        and all platform settings are untouched. Nothing runs automatically and this cannot be undone.
      </p>
      <div className="flex flex-col sm:flex-row gap-3 mt-4">
        <input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder={`Type ${PHRASE}`}
          className="flex-1 px-3 py-2.5 rounded-xl border border-border bg-background outline-none focus:border-destructive text-sm"
          data-testid="reset-confirm-input" />
        <button onClick={run} disabled={busy || confirm !== PHRASE}
          className="px-5 py-2.5 rounded-full bg-destructive text-white font-medium text-sm hover:bg-destructive/90 transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-40"
          data-testid="reset-run-btn">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Delete all user accounts
        </button>
      </div>
    </div>
  );
};

export default AdminUserDataReset;
